<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'
import type { InvoiceRun } from '@/types'
import { Head, useForm } from '@inertiajs/vue3'
import { ref } from 'vue'
import { invoiceRunStatusColor, settlementMonth, statusLabel } from '@/lib/invoices'
import { dashboardLayout } from '@/lib/layouts'
import { formatGrosz } from '@/lib/money'
import { adminInvoiceRunsPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

defineProps<{
  runs: InvoiceRun[]
}>()

const title = 'Invoice runs'
const generateOpen = ref(false)

const form = useForm({
  month: '',
})

function generate() {
  form.post(adminInvoiceRunsPath(), {
    preserveScroll: true,
    onSuccess: () => {
      generateOpen.value = false
      form.reset()
    },
  })
}

const columns: TableColumn<InvoiceRun>[] = [
  { accessorKey: 'settlement_month', header: 'Settlement month' },
  { accessorKey: 'status', header: 'Status' },
  { accessorKey: 'trigger', header: 'Trigger' },
  { accessorKey: 'customers_processed', header: 'Customers processed' },
  { accessorKey: 'invoices_created', header: 'Created' },
  { accessorKey: 'invoices_updated', header: 'Updated' },
  { accessorKey: 'total_amount', header: 'Total' },
  { accessorKey: 'unpriced_line_count', header: 'Unpriced lines' },
  { accessorKey: 'started_at', header: 'Started' },
]

function formatTimestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—'
}
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="invoice-runs">
    <template #header>
      <UDashboardNavbar :title="title">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #right>
          <UButton
            label="Generate now"
            icon="i-lucide-play"
            @click="generateOpen = true"
          />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <UTable
        :data="runs"
        :columns="columns"
        class="ring-default rounded-lg ring"
      >
        <template #settlement_month-cell="{ row }">
          <span class="text-muted text-sm">{{ settlementMonth(row.original.settlement_month) }}</span>
        </template>

        <template #status-cell="{ row }">
          <UBadge
            :color="invoiceRunStatusColor(row.original.status)"
            variant="subtle"
            size="sm"
          >
            {{ statusLabel(row.original.status) }}
          </UBadge>
        </template>

        <template #total_amount-cell="{ row }">
          <span class="tabular-nums">{{ formatGrosz(row.original.total_amount) }}</span>
        </template>

        <template #unpriced_line_count-cell="{ row }">
          <span
            class="tabular-nums"
            :class="row.original.unpriced_line_count > 0 ? 'text-warning font-medium' : 'text-muted'"
          >
            {{ row.original.unpriced_line_count }}
          </span>
        </template>

        <template #started_at-cell="{ row }">
          <span class="text-muted text-sm">{{ formatTimestamp(row.original.started_at) }}</span>
        </template>

        <template #empty>
          <div class="text-muted py-6 text-center text-sm">
            No invoice runs yet.
          </div>
        </template>
      </UTable>
    </template>
  </UDashboardPanel>

  <UModal
    v-model:open="generateOpen"
    title="Generate invoices"
    description="Enqueue invoice generation for a settlement month. Leave blank to settle last month."
  >
    <template #body>
      <UFormField label="Month" name="month" description="Format: YYYY-MM.">
        <UInput
          v-model="form.month"
          placeholder="2026-06"
          class="w-full"
        />
      </UFormField>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="generateOpen = false"
        />
        <UButton
          label="Generate"
          icon="i-lucide-play"
          :loading="form.processing"
          @click="generate"
        />
      </div>
    </template>
  </UModal>
</template>
